import React from "react";
import { Button as Button$1, CircularProgress, IconButton as IconButton$1, Box, Chip } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { LocalizedText } from "../i18n/LocalizedText.js";
import "../icons/Icon.js";
import { CustomIcon } from "../icons/types.js";
import "../icons/ExpandMoreIcon.js";
import { StyledTooltip } from "../tooltip/Tooltip.js";
import "moment-timezone";
import "react-intl";
import "../label/EmptyLabel.js";
import "../label/LabelHoc.js";
import "../tooltip/AutoAdjustedWidthTooltip.styles.js";
import "../tooltip/AutoAdjustedComplexTooltip.styles.js";
import "../tooltip/MultiLineAutoAdjustedWidthTooltip.js";
const iconButtonSmallSize = 32;
const iconButtonMediumSize = 36;
const iconButtonLargeSize = 40;
const useStyles = makeStyles()((theme) => {
  const { fontStyle, fontWeight, fontSize, lineHeight, letterSpacing, textTransform, textAlign } = theme.typography.button;
  return {
    //because of the default size of materialui button is medium and we used it in a lot of places with height 40
    //and for the outlined small button we used height 28, there is a need that fits between small and medium.
    //maybe we can consider refactoring medium to large, and smallPlus to medium when design team finish core lib.
    smallPlusButton: {
      height: theme.custom.smallPlusButtonHeight,
      minWidth: "unset",
      padding: theme.spacing(1, 2)
    },
    buttonWithTotal: {
      display: "flex"
    },
    iconButton: {
      ...theme.custom.button,
      fontStyle,
      fontWeight,
      fontSize,
      lineHeight,
      letterSpacing,
      textTransform,
      textAlign,
      color: theme.palette.secondary.main,
      "&:hover": {
        color: theme.palette.primary.main,
        background: "transparent"
      },
      "&:focus, &:active": {
        color: theme.palette.primary.main,
        background: "transparent",
        borderColor: theme.palette.primary.main
      },
      "&:disabled": {
        color: theme.palette.secondary.main,
        background: "transparent",
        borderColor: theme.custom.palette.content.liner
      },
      "& .MuiSvgIcon-root": {
        width: "20px",
        height: "20px"
      }
    },
    iconButtonSmall: {
      width: iconButtonSmallSize,
      height: iconButtonSmallSize,
      minWidth: iconButtonSmallSize,
      padding: 0
    },
    iconButtonMedium: {
      width: iconButtonMediumSize,
      height: iconButtonMediumSize,
      minWidth: iconButtonMediumSize,
      padding: 0
    },
    iconButtonLarge: {
      width: iconButtonLargeSize,
      height: iconButtonLargeSize,
      minWidth: iconButtonLargeSize,
      padding: 0
    },
    iconButtonRound: {
      background: theme.palette.background.paper,
      border: `1px solid ${theme.custom.palette.content.liner}`,
      boxShadow: "0px 1px 1px rgba(19, 64, 106, 0.06)",
      boxSizing: "border-box",
      borderRadius: "100%",
      padding: 0
    },
    iconButtonOutlined: {
      background: theme.palette.background.paper,
      border: `1px solid ${theme.custom.palette.content.liner}`,
      boxShadow: "0px 1px 1px rgba(19, 64, 106, 0.06)",
      boxSizing: "border-box",
      borderRadius: theme.custom.defaultBorderRadius * 2
    },
    iconButtonSquare: {
      background: theme.palette.background.paper,
      border: `1px solid ${theme.custom.palette.content.liner}`,
      boxShadow: "0px 1px 1px rgba(19, 64, 106, 0.06)",
      boxSizing: "border-box",
      borderRadius: 6
    },
    chip: {
      borderRadius: 21,
      background: "#F0F2F5",
      color: theme.palette.secondary.main,
      marginLeft: theme.spacing(1),
      fontSize: "12px",
      lineHeight: "18px"
    },
    selectButton: {
      display: "flex",
      height: theme.custom.denseButtonHeight,
      ...theme.typography.body1,
      "&:hover, &:focus, &:active": {
        color: theme.palette.text.primary,
        borderColor: theme.custom.palette.content.border
      }
    },
    selectButtonLabel: {
      ...theme.typography.body1,
      color: theme.palette.text.secondary,
      "&:hover": {
        color: theme.palette.text.secondary
      }
    },
    selectButtonEndIcon: {
      fontSize: "10px !important",
      color: theme.palette.text.secondary,
      paddingLeft: theme.spacing(1)
    },
    selectButtonStartIconContainer: {
      display: "flex"
    },
    selectButtonStartIcon: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginRight: theme.spacing(1)
    },
    selectButtonOpen: {
      color: theme.palette.primary.main
    },
    buttonFullWidth: {
      width: "100%"
    },
    buttonAutoWidth: {
      width: "auto"
    },
    stretchButton: {
      width: "100%",
      textAlign: "left",
      border: "none",
      borderRadius: 0,
      borderBottom: `1px solid ${theme.palette.secondary.light}`,
      justifyContent: "space-between",
      "&.MuiButton-sizeMedium > .MuiButton-icon > *:first-child": {
        fontSize: theme.typography.h6.fontSize
      },
      "&:hover .MuiButton-iconSizeMedium > *:first-child": {
        color: theme.palette.primary.main
      }
    },
    buttonCircularProgressPrimary: {
      color: theme.palette.primary.contrastText
    },
    buttonCircularProgressSecondary: {
      color: theme.palette.secondary.contrastText
    },
    buttonFullHeight: {
      height: "100%"
    },
    buttonWithoutBorder: {
      border: "none",
      boxShadow: "none",
      "&:hover, &:focus, &:active": {
        color: theme.palette.text.primary
      }
    }
  };
});
const Button = React.memo(
  ({
    label,
    className,
    children,
    fullWidth,
    autoWidth,
    placement = "default",
    loading,
    color = "secondary",
    fullHeight,
    startIcon,
    withoutBorder,
    disabled,
    ...props
  }) => {
    const { classes: styles, cx } = useStyles();
    return /* @__PURE__ */ React.createElement(
      Button$1,
      {
        className: cx(
          {
            [styles.buttonFullWidth]: fullWidth,
            [styles.buttonAutoWidth]: autoWidth,
            [styles.buttonFullHeight]: fullHeight,
            [styles.stretchButton]: placement === "stretch",
            [styles.buttonWithoutBorder]: withoutBorder
          },
          className
        ),
        color,
        variant: "contained",
        disableRipple: true,
        disableFocusRipple: true,
        disableTouchRipple: true,
        disabled: loading || disabled,
        startIcon: loading ? /* @__PURE__ */ React.createElement(
          CircularProgress,
          {
            size: 12,
            className: cx({
              [styles.buttonCircularProgressPrimary]: color === "primary",
              [styles.buttonCircularProgressSecondary]: color === "secondary"
            })
          }
        ) : startIcon,
        ...props
      },
      label ? /* @__PURE__ */ React.createElement(LocalizedText, { label }) : null,
      children
    );
  }
);
const OutlinedButton = React.memo(({ smallPlus, className, ...props }) => {
  const { classes: styles } = useStyles();
  const smallPlusClass = smallPlus ? styles.smallPlusButton : "";
  return /* @__PURE__ */ React.createElement(Button, { color: "primary", className: `${smallPlusClass} ${className}`, ...props, variant: "outlined" });
});
const IconButton = React.memo(
  ({ iconClassName, children, size = "large", variant = "default", iconFontSize = 14, ...props }) => {
    const { classes: styles, cx } = useStyles();
    return /* @__PURE__ */ React.createElement(
      IconButton$1,
      {
        disableRipple: true,
        disableFocusRipple: true,
        disableTouchRipple: true,
        className: cx(styles.iconButton, {
          [styles.iconButtonRound]: variant === "round",
          [styles.iconButtonOutlined]: variant === "outlined",
          [styles.iconButtonSmall]: size === "small",
          [styles.iconButtonMedium]: size === "medium",
          [styles.iconButtonLarge]: size === "large"
        }),
        size,
        ...props
      },
      children ?? /* @__PURE__ */ React.createElement(Box, { component: "span", className: iconClassName, fontSize: iconFontSize })
    );
  }
);
const OutlinedButtonWithChip = React.memo(({ total, classes, ...props }) => {
  const { classes: styles } = useStyles();
  return /* @__PURE__ */ React.createElement(OutlinedButton, { classes: { ...classes, root: styles.buttonWithTotal }, ...props }, /* @__PURE__ */ React.createElement(Chip, { label: total, className: styles.chip, size: "small" }));
});
const SelectButtonWithChip = React.memo(({ open, label, selectedText, hasArrow = true, ...props }) => {
  const { classes: styles } = useStyles();
  const arrow = open ? CustomIcon.ArrowUp : CustomIcon.ArrowDown;
  const openStyle = open ? styles.selectButtonOpen : "";
  return /* @__PURE__ */ React.createElement(
    Button,
    {
      "data-testid": "selectButtonWithChip",
      className: `${styles.selectButton} ${openStyle}`,
      color: "primary",
      ...props,
      startIcon: label || props.startIcon ? /* @__PURE__ */ React.createElement(Box, { className: styles.selectButtonStartIconContainer }, props.startIcon ? /* @__PURE__ */ React.createElement(Box, { className: styles.selectButtonStartIcon }, props.startIcon) : null, /* @__PURE__ */ React.createElement(Box, { component: "span", className: styles.selectButtonLabel }, label ? /* @__PURE__ */ React.createElement(LocalizedText, { label }) : null)) : null,
      label: selectedText,
      endIcon: hasArrow ? /* @__PURE__ */ React.createElement(Box, { component: "span", className: `${arrow} ${styles.selectButtonEndIcon} ${openStyle}` }) : null,
      variant: "outlined"
    }
  );
});
const SquareIconButton = React.memo(({ iconClassName, size, tooltip, iconFontSize = 13, className, ...props }) => {
  const { classes: styles } = useStyles();
  const buttonSizeStyle = size === "small" ? styles.iconButtonSmall : styles.iconButtonLarge;
  return /* @__PURE__ */ React.createElement(
    StyledTooltip,
    {
      element: /* @__PURE__ */ React.createElement(
        IconButton$1,
        {
          disableRipple: true,
          disableFocusRipple: true,
          disableTouchRipple: true,
          className: `${styles.iconButton} ${buttonSizeStyle} ${styles.iconButtonSquare} ${className}`,
          size: "small",
          ...props
        },
        /* @__PURE__ */ React.createElement(Box, { component: "span", className: iconClassName, fontSize: iconFontSize })
      ),
      title: /* @__PURE__ */ React.createElement(Box, null, tooltip ? /* @__PURE__ */ React.createElement(LocalizedText, { label: tooltip }) : null),
      hideTooltip: !tooltip
    }
  );
});
export {
  Button,
  IconButton,
  OutlinedButton,
  OutlinedButtonWithChip,
  SelectButtonWithChip,
  SquareIconButton,
  Button as default,
  iconButtonLargeSize,
  iconButtonMediumSize,
  iconButtonSmallSize
};
//# sourceMappingURL=Buttons.js.map
